import { FC, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import MultiSelectInput from '@/components/functional/MultiSelectInput/MultiSelectInput.tsx';

import { Alert, Button } from 'react-daisyui';
import useSaveBusinessOnboarding from '@/api/useSaveBusinessOnboarding.ts';
import useStorage from '@/api/useStorage.ts';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/utils/analytics.ts';
import { useState } from 'react';
import Calendar from '@/components/shared/Calendar/Calendar.tsx';
import { formatCareTypes, formatSkills } from '@/utils/formatCareType.ts';
import supabaseClient from '@/api/supabaseClient.ts';
import { CARE_TYPE_OPTIONS, ALL_SKILLS } from '@/constants/careSkills.ts';

const steps = ['profile', 'services', 'availability', 'documents', 'summary'] as const;

const profileStepSchema = z.object({
  photo_url: z.string().optional(),
  bio: z.string().optional(),
  care_types: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
});

type ProfileStepSchema = z.infer<typeof profileStepSchema>;

const servicesStepSchema = z.object({
  hourly_rate: z.coerce.number().positive({ message: 'Must be a positive number' }),
  service_area: z.string().min(1, { message: 'Required' }),
});

type ServicesStepSchema = z.infer<typeof servicesStepSchema>;

const availabilityStepSchema = z.object({
  availability_dates: z.any().optional(),
});

type AvailabilityStepSchema = z.infer<typeof availabilityStepSchema>;

const documentsStepSchema = z.object({
  dbs_document_url: z.string().url({ message: 'Enhanced DBS document is required' }).min(1, { message: 'Enhanced DBS document is required' }),
  insurance_document_url: z.string().url({ message: 'Insurance document is required' }).min(1, { message: 'Insurance document is required' }),
});

type DocumentsStepSchema = z.infer<typeof documentsStepSchema>;

type OnboardingData = ProfileStepSchema & ServicesStepSchema & AvailabilityStepSchema & DocumentsStepSchema & { email?: string };

const BusinessOnboardingPage: FC = () => {
  const { session, currentUser, logout } = useAuthContext();
  const navigate = useNavigate();

  const isBusinessProvider = currentUser?.role === 'business_provider';

  useEffect(() => {
    if (session && !isBusinessProvider) {
      navigate(Routes.CARERS, { replace: true });
    }
  }, [session, isBusinessProvider, navigate]);

  useEffect(() => {
    if (!session?.user?.email) return;
    try {
      const raw = sessionStorage.getItem('business-onboarding:data');
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored?.email && stored.email !== session.user.email) {
          sessionStorage.removeItem('business-onboarding:step');
          sessionStorage.removeItem('business-onboarding:data');
        }
      }
    } catch {
      // ignore
    }
  }, [session?.user?.email]);

  if (session && !isBusinessProvider) {
    return null;
  }

  return (
    <div className="w-full min-h-[80vh] flex items-center justify-center py-8 md:py-12">
      <Stepper.Wrapper steps={steps} initialStep="profile" persistKey="business-onboarding">
        <Stepper.Step<typeof steps> step="profile">
          <StepProfile onLogout={logout} />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="services">
          <StepServices onLogout={logout} />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="availability">
          <StepAvailability onLogout={logout} />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="documents">
          <StepDocuments onLogout={logout} />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="summary">
          <StepSummary onLogout={logout} />
        </Stepper.Step>
      </Stepper.Wrapper>
    </div>
  );
};

export default BusinessOnboardingPage;

type StepProps = { onLogout: () => void };

function SignOutButton({ onLogout }: { onLogout: () => void }) {
  return (
    <button
      type="button"
      onClick={onLogout}
      className="text-sm text-red-400 hover:text-red-600 underline underline-offset-2 transition-colors"
    >
      Sign out
    </button>
  );
}

function ProgressBar() {
  const { step } = useStepperContext<typeof steps, OnboardingData>();

  const stepIndex = steps.indexOf(step as (typeof steps)[number]);

  return (
    <div className="w-full flex items-center gap-2 text-xs text-gray-500">
      {steps.map((s, index) => (
        <div key={s} className="flex-1 flex items-center gap-2">
          <div
            className={`h-[2px] flex-1 ${index <= stepIndex ? 'bg-black' : 'bg-gray-200'}`}
          />
          {index < steps.length - 1 ? null : (
            <div className={`w-2 h-2 rounded-full ${stepIndex === steps.length - 1 ? 'bg-black' : 'bg-gray-300'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

const careTypeOptions = CARE_TYPE_OPTIONS;
const skillOptions = ALL_SKILLS;


function StepProfile({ onLogout }: StepProps) {
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const [searchParams] = useSearchParams();
  const { session } = useAuthContext();
  const email = searchParams.get('email') ?? contextData?.email ?? session?.user?.email ?? undefined;
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string>('');
  const [selectedCareTypes, setSelectedCareTypes] = useState<string[]>(contextData?.care_types ?? []);
  const [selectedSkills, setSelectedSkills] = useState<string[]>(contextData?.skills ?? []);
  const [careTypesOpen, setCareTypesOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const storage = useStorage('avatars');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const careTypesRef = useRef(selectedCareTypes);
  const skillsRef = useRef(selectedSkills);
  useEffect(() => { careTypesRef.current = selectedCareTypes; }, [selectedCareTypes]);
  useEffect(() => { skillsRef.current = selectedSkills; }, [selectedSkills]);

  const { control, handleSubmit, setError, getValues } = useForm<ProfileStepSchema>({
    resolver: zodResolver(profileStepSchema),
    defaultValues: {
      photo_url: contextData?.photo_url ?? '',
      bio: contextData?.bio ?? '',
      care_types: contextData?.care_types ?? [],
      skills: contextData?.skills ?? [],
    },
  });

  useEffect(() => {
    return () => {
      const { photo_url: _ignored, ...restValues } = getValues();
      setContextData((prev) => ({
        ...prev,
        ...restValues,
        care_types: careTypesRef.current,
        skills: skillsRef.current,
      }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoSelect = async (file: File) => {
    if (!session?.user?.id) return;
    setIsUploadingPhoto(true);
    setPhotoError('');
    try {
      const fileName = `${session.user.id}_${Date.now()}.${file.name.split('.').pop()}`;
      await storage.store.mutateAsync({ file, path: fileName });
      const photoUrl = storage.getPublicUrl(fileName);
      setContextData((prev) => ({ ...prev, photo_url: photoUrl }));
    } catch (error) {
      console.error('Error uploading photo:', error);
      setPhotoError('Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleNext = (data: ProfileStepSchema) => {
    if (!contextData?.photo_url) {
      setPhotoError('Photo is required');
      setError('photo_url', { message: 'Photo is required' });
      return;
    }
    setPhotoError('');
    const { photo_url: _ignored, ...restData } = data;
    setContextData((prev) => ({
      ...prev,
      ...restData,
      care_types: selectedCareTypes,
      skills: selectedSkills,
      email: email ?? prev?.email,
    }));
    goToStep('services');
  };

  return (
    <BrandCardSideContent topRight={<SignOutButton onLogout={onLogout} />}>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleNext)}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">Tell us what support can you provide.</h1>
              <p className="text-sm text-text-secondary">You can update this later.</p>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handlePhotoSelect(file);
                if (e.target) e.target.value = '';
              }}
              accept="image/*"
              className="hidden"
            />
            {!contextData?.photo_url ? (
              <>
                <button
                  type="button"
                  className={`btn btn-ghost border ${photoError ? 'border-error' : 'border-gray-300'}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? 'Uploading…' : '+ Upload photo'}
                </button>
                {photoError && <p className="text-error text-sm">{photoError}</p>}
              </>
            ) : (
              <div className="relative">
                <img
                  src={contextData.photo_url}
                  alt="Preview"
                  className="w-full h-48 object-contain"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-100 border border-gray-200"
                    onClick={() => fileInputRef.current?.click()}
                    title="Change photo"
                    disabled={isUploadingPhoto}
                  >
                    {isUploadingPhoto ? '…' : '✎'}
                  </button>
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-100 border border-gray-200 text-error"
                    onClick={() => {
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      setContextData((prev) => ({ ...prev, photo_url: '' }));
                    }}
                    title="Remove photo"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            <Controller
              control={control}
              name="bio"
              render={({ field: { value, onChange }, formState: { errors } }) => (
                <TextArea
                  value={value}
                  onChange={onChange}
                  placeholder="Add your bio..."
                  error={errors.bio?.message}
                  rows={5}
                />
              )}
            />

            {/* Care Types */}
            <MultiSelectInput
              value={selectedCareTypes}
              onChange={setSelectedCareTypes}
              options={careTypeOptions}
              placeholder="Type of service"
              isOpen={careTypesOpen}
              onToggle={() => { setCareTypesOpen((o) => !o); setSkillsOpen(false); }}
              onClose={() => setCareTypesOpen(false)}
            />

            {/* Skills */}
            <MultiSelectInput
              value={selectedSkills}
              onChange={setSelectedSkills}
              options={skillOptions}
              placeholder="Skills"
              isOpen={skillsOpen}
              onToggle={() => { setSkillsOpen((o) => !o); setCareTypesOpen(false); }}
              onClose={() => setSkillsOpen(false)}
            />
          </div>
          <LoadingButton type="submit" color="primary" loading={false}>
            Continue
          </LoadingButton>
        </form>
      </div>
    </BrandCardSideContent>
  );
}

function StepServices({ onLogout }: StepProps) {
  const { goToStep, setContextData, contextData, previousStep } = useStepperContext<typeof steps, OnboardingData>();
  const { currentUser } = useAuthContext();
  const registeredPostcode = currentUser?.postcode ?? '';

  const {
    control,
    handleSubmit,
    getValues: getServicesValues,
    setValue,
  } = useForm<ServicesStepSchema>({
    resolver: zodResolver(servicesStepSchema),
    defaultValues: {
      hourly_rate: contextData?.hourly_rate ?? ('' as any),
      service_area: registeredPostcode || (contextData?.service_area ?? ''),
    },
  });

  // currentUser (and its postcode) loads asynchronously after login/verification, so it
  // may not be ready yet when this form's defaultValues are captured at mount. Sync the
  // field once it arrives, so the registration postcode always ends up displayed here.
  useEffect(() => {
    if (registeredPostcode) {
      setValue('service_area', registeredPostcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredPostcode]);

  useEffect(() => {
    return () => {
      setContextData((prev) => ({ ...prev, ...getServicesValues() }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = (data: ServicesStepSchema) => {
    setContextData((prev) => ({ ...prev, ...data }));
    goToStep('availability');
  };

  return (
    <BrandCardSideContent topRight={<SignOutButton onLogout={onLogout} />}>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleNext)}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">Tell us what support can you provide.</h1>
              <p className="text-sm text-text-secondary">You can update this later.</p>
            </div>
            <Controller
              control={control}
              name="hourly_rate"
              render={({ field: { value, onChange }, formState: { errors } }) => (
                <TextInput
                  type="number"
                  value={value ?? ''}
                  onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="Hourly rate"
                  error={errors.hourly_rate?.message}
                  startIcon={<span className="text-base text-gray-500 select-none">£</span>}
                  min={0}
                />
              )}
            />
            <Controller
              control={control}
              name="service_area"
              render={({ field: { value, onChange }, formState: { errors } }) => (
                <TextInput
                  value={value}
                  onChange={onChange}
                  placeholder="e.g. HP11, SW1, M1"
                  label="Service area (first 3–4 characters of your postcode)"
                  required
                  error={errors.service_area?.message}
                  maxLength={10}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <LoadingButton type="submit" color="primary" loading={false}>
              Continue
            </LoadingButton>
            <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
              Go back
            </Button>
          </div>
        </form>
      </div>
    </BrandCardSideContent>
  );
}

function StepAvailability({ onLogout }: StepProps) {
  const { goToStep, setContextData, previousStep, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const [startDate, setStartDate] = useState<Date | null>(
    contextData?.availability_dates?.startDate ? new Date(contextData.availability_dates.startDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    contextData?.availability_dates?.endDate ? new Date(contextData.availability_dates.endDate) : null
  );

  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  useEffect(() => { startDateRef.current = startDate; }, [startDate]);
  useEffect(() => { endDateRef.current = endDate; }, [endDate]);

  useEffect(() => {
    return () => {
      setContextData((prev) => ({
        ...prev,
        availability_dates: {
          startDate: startDateRef.current?.toISOString(),
          endDate: endDateRef.current?.toISOString(),
        },
      }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateSelect = (date: Date, isStart: boolean) => {
    if (isStart) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (startDate && date < startDate) {
        setStartDate(date);
        setEndDate(startDate);
      } else {
        setEndDate(date);
      }
    }
  };

  const handleNext = () => {
    const availabilityData = {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
    };
    setContextData((prev) => ({ ...prev, availability_dates: availabilityData }));
    goToStep('documents');
  };

  return (
    <BrandCardSideContent topRight={<SignOutButton onLogout={onLogout} />}>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">Set Your Availability</h1>
              <p className="text-sm text-text-secondary">You can update this later.</p>
            </div>
            <Calendar 
              startDate={startDate} 
              endDate={endDate} 
              onDateSelect={handleDateSelect}
            />
          </div>
          <div className="flex flex-col gap-2">
            <LoadingButton type="button" color="primary" loading={false} onClick={handleNext}>
              Continue
            </LoadingButton>
            <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
              Go back
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

function StepDocuments({ onLogout }: StepProps) {
  const { goToStep, setContextData, previousStep, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const { session } = useAuthContext();
  const [dbsFile, setDbsFile] = useState<File | null>(null);
  const [insuranceFile, setInsuranceFile] = useState<File | null>(null);
  const [dbsError, setDbsError] = useState<string>('');
  const [insuranceError, setInsuranceError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const dbsInputRef = useRef<HTMLInputElement>(null);
  const insuranceInputRef = useRef<HTMLInputElement>(null);
  const documentsStorage = useStorage('documents');

  const handleNext = async () => {
    // Validate both documents are uploaded
    let hasError = false;
    
    if (!dbsFile && !contextData?.dbs_document_url) {
      setDbsError('Enhanced DBS document is required');
      hasError = true;
    } else {
      setDbsError('');
    }
    
    if (!insuranceFile && !contextData?.insurance_document_url) {
      setInsuranceError('Insurance document is required');
      hasError = true;
    } else {
      setInsuranceError('');
    }
    
    if (hasError) {
      return;
    }

    setIsUploading(true);
    let dbsUrl = '';
    let insuranceUrl = '';

    // Upload DBS document if selected
    if (dbsFile && session?.user?.id) {
      try {
        const fileExt = dbsFile.name.split('.').pop();
        const fileName = `${session.user.id}/dbs_${Date.now()}.${fileExt}`;
        
        // Upload to documents bucket
        await documentsStorage.store.mutateAsync({
          file: dbsFile,
          path: fileName,
        });
        dbsUrl = fileName;

        // Create record in documents table
        await supabaseClient.functions.invoke('upload_document', {
          method: 'POST',
          body: {
            document_type: 'dbs',
            file_name: dbsFile.name,
            file_url: fileName,
          },
        });
      } catch (error) {
        console.error('Error uploading DBS document:', error);
        setIsUploading(false);
        return;
      }
    }

    // Upload insurance document if selected
    if (insuranceFile && session?.user?.id) {
      try {
        const fileExt = insuranceFile.name.split('.').pop();
        const fileName = `${session.user.id}/insurance_${Date.now()}.${fileExt}`;
        
        // Upload to documents bucket
        await documentsStorage.store.mutateAsync({
          file: insuranceFile,
          path: fileName,
        });
        insuranceUrl = fileName;

        // Create record in documents table
        await supabaseClient.functions.invoke('upload_document', {
          method: 'POST',
          body: {
            document_type: 'insurance',
            file_name: insuranceFile.name,
            file_url: fileName,
          },
        });
      } catch (error) {
        console.error('Error uploading insurance document:', error);
        setIsUploading(false);
        return;
      }
    }

    setIsUploading(false);
    setContextData((prev) => ({ 
      ...prev,
      dbs_document_url: dbsUrl,
      insurance_document_url: insuranceUrl 
    }));
    goToStep('summary');
  };

  return (
    <BrandCardSideContent topRight={<SignOutButton onLogout={onLogout} />}>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">Upload both documents to get verified.</h1>
              <p className="text-sm text-text-secondary">You can check states later.</p>
            </div>
            <input
              ref={dbsInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                setDbsFile(e.target.files?.[0] || null);
                setDbsError('');
                if (e.target) e.target.value = '';
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dbsInputRef.current?.click();
              }}
              className={`w-full py-3 px-4 border-2 ${dbsError ? 'border-error' : 'border-black'} bg-white text-black rounded-xl font-medium text-base hover:bg-gray-50 transition-colors`}
            >
              {dbsFile || contextData?.dbs_document_url ? '✓ Change Enhanced DBS document' : '+ Upload Enhanced DBS'}
            </button>
            {dbsFile && (
              <p className="text-sm text-gray-600">Selected: {dbsFile.name}</p>
            )}
            {!dbsFile && contextData?.dbs_document_url && (
              <p className="text-sm text-green-600">✓ Enhanced DBS document uploaded</p>
            )}
            {dbsError && (
              <p className="text-error text-sm">{dbsError}</p>
            )}
            <input
              ref={insuranceInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                setInsuranceFile(e.target.files?.[0] || null);
                setInsuranceError('');
                if (e.target) e.target.value = '';
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insuranceInputRef.current?.click();
              }}
              className={`w-full py-3 px-4 border-2 ${insuranceError ? 'border-error' : 'border-black'} bg-white text-black rounded-xl font-medium text-base hover:bg-gray-50 transition-colors`}
            >
              {insuranceFile || contextData?.insurance_document_url ? '✓ Change insurance documents' : '+ Upload insurance documents'}
            </button>
            {insuranceFile && (
              <p className="text-sm text-gray-600">Selected: {insuranceFile.name}</p>
            )}
            {!insuranceFile && contextData?.insurance_document_url && (
              <p className="text-sm text-green-600">✓ Insurance document uploaded</p>
            )}
            {insuranceError && (
              <p className="text-error text-sm">{insuranceError}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <LoadingButton type="button" color="primary" loading={isUploading} onClick={handleNext}>
              Continue
            </LoadingButton>
            <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
              Go back
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

function StepSummary({ onLogout }: StepProps) {
  const { contextData, previousStep } = useStepperContext<typeof steps, OnboardingData>();
  const [searchParams] = useSearchParams();
  const { session } = useAuthContext();
  const email = searchParams.get('email') ?? contextData?.email ?? session?.user?.email ?? undefined;
  const navigate = useNavigate();
  const { mutateAsync: saveOnboarding, isPending, isError, error, isSuccess } = useSaveBusinessOnboarding();
  const queryClient = useQueryClient();

  const handleSave = () => {
    if (!email) return;

    void (async () => {
      try {
        const payload = {
          email,
          photo_url: contextData?.photo_url,
          bio: contextData?.bio,
          care_types: contextData?.care_types,
          skills: contextData?.skills,
          hourly_rate: contextData?.hourly_rate,
          service_area: contextData?.service_area,
          availability_dates: contextData?.availability_dates,
          dbs_document_url: contextData?.dbs_document_url,
          insurance_document_url: contextData?.insurance_document_url,
        };

        await saveOnboarding(payload);
        trackEvent({ name: 'onboarding_completed', params: { user_role: 'carer' } });
        queryClient.setQueryData(['business-onboarding', email], payload);
        // Navigate to the first tab in navigation (intro calls page)
        navigate(Routes.INTRO_CALLS);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const summaryItems = [
    { label: 'Photo', value: contextData?.photo_url ? 'Uploaded ✓' : 'Not uploaded' },
    { label: 'Bio', value: contextData?.bio || 'Not provided' },
    { label: 'Care Types', value: formatCareTypes(contextData?.care_types) },
    { label: 'Skills', value: formatSkills(contextData?.skills) },
    { label: 'Hourly rate', value: contextData?.hourly_rate ? `£${contextData.hourly_rate}` : 'Not set' },
    { label: 'Service Area', value: contextData?.service_area || 'Not set' },
    { 
      label: 'Availability', 
      value: contextData?.availability_dates?.startDate && contextData?.availability_dates?.endDate
        ? `${new Date(contextData.availability_dates.startDate).toLocaleDateString()} - ${new Date(contextData.availability_dates.endDate).toLocaleDateString()}`
        : 'Not set'
    },
    { label: 'Enhanced DBS Document', value: contextData?.dbs_document_url ? 'Uploaded ✓' : 'Not uploaded' },
    { label: 'Insurance Document', value: contextData?.insurance_document_url ? 'Uploaded ✓' : 'Not uploaded' },
  ];

  return (
    <BrandCardSideContent topRight={<SignOutButton onLogout={onLogout} />}>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">Your profile will go live once verified.</h1>
          <p className="text-sm text-text-secondary">Summary:</p>
        </div>
        {isError && <Alert status="error">{error?.message}</Alert>}
        {isSuccess && <Alert status="success">Profile saved successfully!</Alert>}
        <div className="border border-dashed border-gray-300 rounded-xl p-4 flex flex-col gap-2">
          {summaryItems.map((item) => (
            <div key={item.label} className="flex items-start gap-2 text-sm">
              <span className="font-semibold">{item.label}:</span>
              <span className="text-text-secondary">{item.value || '-'}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <LoadingButton type="button" color="primary" loading={isPending} onClick={handleSave}>
            Save
          </LoadingButton>
          <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
            Go back
          </Button>
        </div>
      </div>
    </BrandCardSideContent>
  );
}
